'use client';

import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';
import { useVariantsStore } from '../../stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type ProductPriceProps = {
  className?: {
    main?: string;
    discount?: string;
    discountWrapper?: string;
    price?: string;
    priceAfterDiscount?: string;
    priceBeforeDiscount?: string;
  };
};

export function ProductPrice({ className }: ProductPriceProps) {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore((s) => s.templateSettings);

  const selectedVariant = useVariantsStore((state) => state.selectedVariant);
  const product = useVariantsStore((s) => s.product);

  const me = useCustomerStore((state) => state.me);
  const desiredObject = product.variants && product.variants.length > 0 ? selectedVariant : product;
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, {
    discountDecimalPlaces: 0,
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });

  const unit = templateSettings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';

  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';

  const isDiscounted = priceDisplay.showPreviousPrice;

  function formatPrice(price: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(price)
      .replace('.', ' '); // Replace the dot with a space
  }

  if (priceDisplay.priceOnRequest) {
    return <span className="font-heading text-xl font-medium text-primary">{t('text2278')}</span>;
  }

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between',
        'ea-product-price',
        className?.main,
      )}
    >
      <div className="flex flex-col items-center">
        {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
          <span className="whitespace-nowrap text-sm text-[#808080]">
            {`${t('text1926')} ${formatPrice(priceDisplay.mocPrice)}${unit}`}
          </span>
        ) : null}
        <div
          className={cn(
            'flex flex-col items-start justify-start gap-1 sm:flex-row sm:items-center lg:gap-2 xl:gap-3',
            'ea-product-price__price',
            className?.price,
          )}
        >
          <div
            data-ea-visual-tag="h3"
            className={cn(
              'font-heading uppercase',
              'whitespace-nowrap text-3xl font-medium text-primary lg:text-4xl',
              'ea-product-price__price-after-discount',
              className?.priceAfterDiscount,
            )}
          >
            {formatPrice(priceDisplay.current ?? 0)}
          </div>
          {isDiscounted ? (
            <del
              className={cn(
                'font-heading uppercase',
                'text-lg font-normal text-[#808080] sm:text-xl lg:text-2xl',
                'ea-product-price__price-before-discount',
                className?.priceBeforeDiscount,
              )}
            >
              {formatPrice(priceDisplay.beforeDiscount ?? 0)}
            </del>
          ) : null}
        </div>
        {priceDisplay.showMocVocPrices && priceDisplay.secondary != null ? (
          <span className="whitespace-nowrap text-sm text-[#808080]">
            {`${t('text1925')} ${formatPrice(priceDisplay.secondary)}${unit} ${
              priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')
            }`}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col items-end justify-end gap-2">
        {isDiscounted && Number(priceDisplay.discountLabel) > 0 ? (
          <div
            className={cn(
              'bg-[#b32d2d] px-2 py-1.5 font-heading text-sm uppercase text-white lg:text-base',
            )}
          >
            Zľava -{priceDisplay.discountLabel}%
          </div>
        ) : null}
        {hasSpecialProductStatus ? (
          <ProductStatusText status={product.status} />
        ) : (
          <div
            className={cn(
              'font-heading uppercase',
              'px-2 py-1.5 text-[10px] font-normal text-white sm:text-xs lg:text-sm xl:text-base',
              selectedVariant?.availability.name === 'Skladom' ? 'bg-[#45CC02]' : 'bg-[#FFA500]',
              !selectedVariant &&
                (product.availability.name === 'Skladom' ? 'bg-[#45CC02]' : 'bg-[#FFA500]'),
            )}
          >
            {selectedVariant?.availability.name ?? product.availability.name}
          </div>
        )}
      </div>
    </div>
  );
}
